import type { FormikHelpers } from 'formik';
import type { FormInput, IBaseInput, IDropdownOption, IDropdownOptionGroup } from '~/types/inputs';
import type { IAPIFormInput } from '~/types/api';
import { RESULT_PAGE_ROUTE, THANK_YOU_ROUTE } from '~/constants';

export const getInputWidth = (rowLength: number): IBaseInput['width'] => {
	switch (rowLength) {
		case 3:
			return 'third';
		case 2:
			return 'half';
		case 1:
		default:
			return 'full';
	}
};

export const getInputSizeClasses = (width: IBaseInput['width']) => {
	return {
		'col-span-6': width === 'full',
		'col-span-6 sm:col-span-3': width === 'half',
		'col-span-6 sm:col-span-2': width === 'third',
	};
};

export const parseInputType = (input: IAPIFormInput): FormInput['type'] => {
	if (input.name === 'date_of_birth') {
		return 'dob';
	}

	// The type on an verification_code input is text, so we need to override it
	if (input.name === 'verification_code') {
		return 'tfa';
	}

	// This is an input that supports AU and NZ states
	// It should be used in a form that has a Country dropdown
	// and it will swap the options out depending on the selected Country
	if (input.name === 'state_or_region') {
		return 'region';
	}

	// The type on an aus_postcode input is text, so we need to override it
	if (input.name === 'aus_postcode') {
		return 'aus_postcode';
	}

	if (input.name === 'dealer') {
		return 'dealer';
	}

	switch (input.type) {
		case 'mobile':
			return 'tel';
		case 'dropdown':
			return 'select';
		case 'receipt_upload':
			return 'file';
		case 'checkbox':
			return 'checkbox';
		case 'image_radio_group':
			return 'image_radio_group';
		case 'heading':
			return 'heading';
		case 'textarea':
			return 'textarea';
		case 'date':
			return 'date';
		case 'radio':
			return 'radio';
		case 'lookup':
			return 'lookup';
		default:
			return 'text';
	}

	return 'text';
};

export type DropdownOptionHybrid = IDropdownOption | IDropdownOptionGroup;

export function isOptionOptionGroup(
	options: DropdownOptionHybrid,
): options is IDropdownOptionGroup {
	return 'groupLabel' in options;
}

const parseDropdownOptions = (options: DropdownOptionHybrid[] | null): DropdownOptionHybrid[] => {
	if (!options || options.length === 0) {
		return [];
	}

	let ungroupedOptions = [
		{
			label: 'Please select',
			value: '',
		},
	];

	const groupedOptions = options.reduce(
		(acc: DropdownOptionHybrid[], curr: DropdownOptionHybrid) => {
			// All ungrouped options should come down with the null label, but we'll check just in case
			if (!isOptionOptionGroup(curr)) {
				ungroupedOptions = [...ungroupedOptions, curr];

				return acc;
			}

			// If no group label, add to ungrouped options
			if (curr.groupLabel === null) {
				ungroupedOptions = [...ungroupedOptions, ...curr.options];

				return acc;
			}

			// Otherwise, push the whole group to the grouped options
			return [...acc, curr];
		},
		[],
	);

	return [...ungroupedOptions, ...groupedOptions];
};

export const processFormItems = (items: IAPIFormInput[][]): FormInput[] => {
	return items.reduce((acc: FormInput[], curr: IAPIFormInput[]) => {
		return [
			...acc,
			...curr
				// The dealer_other field has to be in the form on the API side for reporting,
				// but we don't want to display it because it's handled by the dealer lookup field itself
				.filter(input => input.name !== 'dealer_other')
				.flatMap((input: IAPIFormInput): FormInput => {
					const type = parseInputType(input);

					return {
						type,
						label: input.label,
						name: input.name,
						description: input.description,
						required: input.required,
						width: getInputWidth(curr.length),
						value: input.type === 'checkbox' ? 'false' : '',
						options:
							type === 'image_radio_group' ||
							type === 'radio' ||
							input.name === 'dealer'
								? input.options
								: parseDropdownOptions(input.groupOptions || input.options),
					};
				}),
		];
	}, []);
};

export const ERRORS = {
	RECAPTCHA_ERROR: 'Failed to authenticate your form submission. Please try again.',
	GENERIC_SUBMISSION_ERROR: 'Something went wrong. Please try again.',
	GENERIC_VALIDATION_ERROR:
		'Failed to submit form entry. Please check the form for issues and try again.',
};

export enum FORM_SUBMISSION_ERROR_CODES {
	PROMOTION_FINISHED = 'promotion_finished',
	PROMOTION_NOT_STARTED = 'promotion_not_started',
	VALIDATION_FAILED = 'validation_failed',
}

export type FormSubmissionError =
	| undefined
	| {
			success: false;
			status: number;
			error: { message: string } & (
				| {
						code:
							| FORM_SUBMISSION_ERROR_CODES.PROMOTION_NOT_STARTED
							| FORM_SUBMISSION_ERROR_CODES.PROMOTION_FINISHED;
				  }
				| {
						code: FORM_SUBMISSION_ERROR_CODES.VALIDATION_FAILED;
						errors: {
							[key: string]: string;
						};
				  }
			);
	  };

export const handleError = (
	error: FormSubmissionError,
	setErrors: FormikHelpers<any>['setErrors'],
): string | null => {
	if (import.meta.env.VERCEL_ENV !== 'production') {
		console.log('Error', error);
	}

	if (!error || !error?.error) {
		return ERRORS.GENERIC_SUBMISSION_ERROR;
	}

	const { code } = error.error;

	if (code === FORM_SUBMISSION_ERROR_CODES.VALIDATION_FAILED) {
		if (error.error.errors) {
			setErrors(error.error.errors);
		} else {
			return ERRORS.GENERIC_VALIDATION_ERROR;
		}
	}

	if (code === FORM_SUBMISSION_ERROR_CODES.PROMOTION_FINISHED) {
		window.location.assign('/promotion-closed');
	}

	if (code === FORM_SUBMISSION_ERROR_CODES.PROMOTION_NOT_STARTED) {
		window.location.assign('/coming-soon');
	}

	return ERRORS.GENERIC_SUBMISSION_ERROR;
};

export type AutocompleteNames =
	| 'off'
	| 'on'
	| 'name'
	| 'honorific-prefix'
	| 'given-name'
	| 'family-name'
	| 'email'
	| 'username'
	| 'new-password'
	| 'current-password'
	| 'one-time-code'
	| 'street-address'
	| 'address-line1'
	| 'address-line2'
	| 'address-level4'
	| 'address-level3'
	| 'address-level2'
	| 'address-level1'
	| 'postal-code'
	| 'country'
	| 'language'
	| 'tel'
	| 'tel-country-code';

export const getAutoComplete = (name: string): AutocompleteNames => {
	switch (name) {
		case 'first_name':
			return 'given-name';
		case 'last_name':
			return 'family-name';
		case 'mobile':
			return 'tel';
		case 'email':
		case 'confirm_email':
			return 'email';
		case 'postcode':
			return 'postal-code';
		case 'address_line_1':
			return 'address-line1';
		case 'address_line_2':
			return 'address-line2';
		case 'suburb':
			return 'address-level2';
		case 'state':
			return 'address-level1';
		default:
			return 'off';
	}
};

export function getPostSubmissionRedirection(slug: string, data: any) {
	if (data.data.requiresProfile) {
		return `/${slug}/create-profile`;
	}

	if (data.data.result === 'RESULT_WINNER') {
		return `/${slug}/result`;
	}

	return `/${slug}/thank-you`;
}
